import { useQuery, type QueryClient } from '@tanstack/react-query';

import type { AuthMeResponse } from '@brightlocal/api-client';

import { apiClient } from 'shared/apiClient';
import { ENTITLEMENTS_STALE_TIME } from 'shared/consts';
import { isAuthenticated } from 'shared/services';
import { redirectIfSessionExpired } from 'shared/utils';

/** Query key for the authenticated user profile */
export const AUTH_ME_QUERY_KEY = ['auth-me'] as const;

/*
 * auth/me access — five entry points over one cache key; pick by call-site:
 *   • authMe                     — force a fresh fetch, ignoring freshness (post-login / -signup).
 *   • ensureAuthMe               — seed the cache only if missing (root `_authenticated` guard).
 *   • revalidateAuthMe           — refetch only when stale, then read `subscription.packageId` (post-location onboarding step).
 *   • revalidateAuthMeOrRedirect — `revalidateAuthMe` + session-expiry → router redirect (onboarding route guard).
 *   • useAuthMe                  — reactive access from components.
 */

/** Fetches the auth/me data and caches it in the query client. */
export const authMe = (queryClient: QueryClient): Promise<AuthMeResponse> =>
  queryClient.fetchQuery({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: ({ signal }) => apiClient.user.authMe({ signal })
  });

/** Ensures auth/me data is present in the query client cache, fetching only if missing. */
export const ensureAuthMe = (queryClient: QueryClient): Promise<AuthMeResponse> =>
  queryClient.ensureQueryData({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: ({ signal }) => apiClient.user.authMe({ signal }),
    staleTime: ENTITLEMENTS_STALE_TIME
  });

/**
 * Revalidates auth/me before a plan-gated decision: returns the cached value when it is
 * still fresh (younger than {@link ENTITLEMENTS_STALE_TIME}) and refetches otherwise. Use in
 * route guards / onboarding routing right before reading `subscription.packageId` — the
 * onboarding flow branches on the plan immediately after a mutation, with no focus event to lean on.
 */
export const revalidateAuthMe = (queryClient: QueryClient): Promise<AuthMeResponse> =>
  queryClient.fetchQuery({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: ({ signal }) => apiClient.user.authMe({ signal }),
    staleTime: ENTITLEMENTS_STALE_TIME
  });

/**
 * Route `beforeLoad` guard preamble: revalidates auth/me and converts a session-expiry 401
 * into a router redirect (via {@link redirectIfSessionExpired}); any other error is rethrown.
 * Use in plan-gated guards right before reading `subscription.packageId`.
 */
export const revalidateAuthMeOrRedirect = (queryClient: QueryClient, href: string): Promise<AuthMeResponse> =>
  revalidateAuthMe(queryClient).catch((error: unknown) => redirectIfSessionExpired(error, href));

/** Hook to access the authenticated user, customer, subscription and feature allowance data */
export const useAuthMe = () =>
  useQuery({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: ({ signal }) => apiClient.user.authMe({ signal }),
    enabled: isAuthenticated(),
    staleTime: ENTITLEMENTS_STALE_TIME,
    // Override the global `refetchOnWindowFocus: false`: an out-of-band plan change is
    // picked up on refocus (once past the freshness cap) via the app-wide AppShell observer.
    refetchOnWindowFocus: true
  });
